import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Sparkles } from "lucide-react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import DemoDialog from "@/components/site/DemoDialog";
import { SOLUTIONS_IMAGES } from "@/lib/site";

const SUBPAGE_META = {
  "brokerages":     { title: "Brokerages",     img: SOLUTIONS_IMAGES.cardBrokerages,    tagline: "Capture, qualify, route, and convert more leads with intelligent automation." },
  "developers":     { title: "Developers",     img: SOLUTIONS_IMAGES.cardDevelopers,    tagline: "Unify sales, inventory, marketing, and reporting across projects." },
  "investors":      { title: "Investors",      img: SOLUTIONS_IMAGES.cardInvestors,     tagline: "Portfolio visibility, performance tracking, and data-driven decisions." },
  "property-teams": { title: "Property Teams", img: SOLUTIONS_IMAGES.cardPropertyTeams, tagline: "Streamline operations, engage tenants, and improve efficiency." },
  "enterprises":    { title: "Enterprises",    img: SOLUTIONS_IMAGES.cardEnterprises,   tagline: "Enterprise-grade intelligence, governance, and automation at scale." },
};

export default function SolutionSubpage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [demoOpen, setDemoOpen] = useState(false);
  const meta = SUBPAGE_META[slug];

  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, [slug]);

  if (!meta) {
    return (
      <div className="min-h-screen bg-obsidian text-platinum flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-display text-3xl">Solution not found</h1>
          <button onClick={() => navigate("/solutions")} className="mt-6 btn-primary rounded-md h-11 px-5 text-sm">Back to Solutions</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-obsidian text-platinum" data-testid={`subpage-${slug}`}>
      <Navbar onBookDemo={() => setDemoOpen(true)} />

      <section className="relative pt-[120px] lg:pt-[140px] pb-20 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(ellipse 60% 70% at 75% 35%, rgba(37,99,235,0.18), transparent 70%)" }} aria-hidden />
        <div className="relative mx-auto max-w-[1200px] px-6 lg:px-10">
          <button onClick={() => navigate("/solutions")}
            className="inline-flex items-center gap-2 text-[13px] text-platinum/70 hover:text-platinum mb-8 fade-up">
            <ArrowLeft className="w-4 h-4" /> Back to Solutions
          </button>

          <div className="grid lg:grid-cols-[1fr_1fr] gap-10 lg:gap-14 items-center">
            <div className="fade-up">
              <div className="text-[10.5px] tracking-[0.38em] text-sapphire-glow uppercase font-medium">SOLUTIONS</div>
              <h1 className="font-display mt-4 text-platinum text-[44px] sm:text-[58px] lg:text-[68px] leading-[1.05] tracking-[-0.022em]">
                {meta.title}
              </h1>
              <p className="mt-6 text-[16px] sm:text-[17px] text-platinum/75 leading-[1.7] max-w-[520px]">
                {meta.tagline}
              </p>

              <div className="mt-8 inline-flex items-center gap-2.5 px-4 py-2.5 rounded-full"
                style={{ background: "rgba(96,165,250,0.08)", border: "1px solid rgba(96,165,250,0.32)" }}>
                <Sparkles className="w-4 h-4 text-sapphire-glow" />
                <span className="text-[12.5px] text-platinum/85 tracking-wide">Detailed page coming soon</span>
              </div>

              <div className="mt-9 flex flex-wrap items-center gap-3">
                <button onClick={() => setDemoOpen(true)} data-testid={`${slug}-book-demo`}
                  className="btn-primary rounded-md h-12 px-6 text-[14px] font-medium inline-flex items-center gap-2">
                  Book a Demo <ArrowRight className="w-4 h-4" />
                </button>
                <button onClick={() => navigate("/solutions")} data-testid={`${slug}-back`}
                  className="btn-ghost rounded-md h-12 px-6 text-[14px] font-medium inline-flex items-center gap-2">
                  Explore Other Solutions <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="relative aspect-[4/5] fade-up rounded-2xl overflow-hidden border border-white/[0.08]"
              style={{ animationDelay: "120ms" }}>
              <img src={meta.img} alt={meta.title} className="w-full h-full object-cover" draggable="false" />
              <div className="absolute inset-0"
                style={{ background: "linear-gradient(180deg, rgba(10,15,26,0.20) 0%, rgba(10,15,26,0.55) 100%)" }} />
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <DemoDialog open={demoOpen} onClose={() => setDemoOpen(false)} />
    </div>
  );
}
